import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
// Helper component for the vehicle card
function VehicleCard({
  vehicle
}: {
  vehicle: any;
}) {
  return <Card className="rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 border-none bg-white flex flex-col h-full" data-id="element-372">
      <img src={vehicle.image} alt={vehicle.name} className="w-full h-48 object-cover" data-id="element-373" />
      <div className="p-6 flex flex-col flex-grow" data-id="element-374">
        <div className="flex justify-between items-start gap-2 mb-2" data-id="element-375">
          <h3 className="text-lg font-bold text-black leading-tight min-h-[2.75rem]" data-id="element-376">
            {vehicle.name}
          </h3>
          <span className="bg-warm-100 text-warm-800 text-xs font-bold px-2 py-1 rounded flex-shrink-0" data-id="element-377">
            {vehicle.year}
          </span>
        </div>
        <p className="text-primary-600 font-bold text-lg mb-3" data-id="element-378">
          {vehicle.price}
        </p>
        <p className="text-warm-600 text-sm mb-6 flex-grow line-clamp-2" data-id="element-379">
          {vehicle.description}
        </p>
        <div className="flex gap-3 mt-auto items-stretch" data-id="element-380">
          <Link to="/booking" className="flex-1 flex" data-id="element-381">
            <Button variant="primary" size="sm" className="w-full h-full" data-id="element-382">
              Call
            </Button>
          </Link>
          <Link to="/booking" className="flex-1 flex" data-id="element-383">
            <Button variant="outline" size="sm" className="w-full h-full border-2" data-id="element-384">
              Hire Now
            </Button>
          </Link>
        </div>
      </div>
    </Card>;
}
export function Fleet() {
  const cars = [{
    name: 'Honda Odyssey Accessible',
    year: '2024',
    price: '$45/hr',
    description: 'Modified with side-entry ramp and lowered floor for easy access.',
    image: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0afa?w=600&q=80'
  }, {
    name: 'Toyota Camry Modified',
    year: '2023',
    price: '$40/hr',
    description: 'Comfortable sedan with specialized hand controls and transfer seat.',
    image: 'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=600&q=80'
  }, {
    name: 'Chrysler 200 Accessible',
    year: '2022',
    price: '$38/hr',
    description: 'Compact and efficient with customized accessibility features.',
    image: 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=600&q=80'
  }, {
    name: 'Hyundai Sonata Modified',
    year: '2025',
    price: '$42/hr',
    description: 'Modern sedan equipped with state-of-the-art mobility aids.',
    image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=600&q=80'
  }];
  const vans = [{
    name: 'Chrysler Pacifica Accessible',
    year: '2025',
    price: '$65/hr',
    description: 'Premium minivan with power in-floor ramp and spacious interior.',
    image: 'https://images.unsplash.com/photo-1559416523-140ddc3d238c?w=600&q=80'
  }, {
    name: 'Toyota Sienna Modified',
    year: '2024',
    price: '$60/hr',
    description: 'Hybrid efficiency with manual fold-out ramp and removable seats.',
    image: 'https://images.unsplash.com/photo-1570733577524-3a047079e80d?w=600&q=80'
  }, {
    name: 'Honda Odyssey WAV',
    year: '2023',
    price: '$58/hr',
    description: 'Reliable wheelchair accessible van with rear-entry configuration.',
    image: 'https://images.unsplash.com/photo-1609521263047-f8f205293f24?w=600&q=80'
  }, {
    name: 'Dodge Grand Caravan',
    year: '2022',
    price: '$50/hr',
    description: 'Classic accessible van with side-entry ramp and tie-downs.',
    image: 'https://images.unsplash.com/photo-1543465077-db45d34b88a5?w=600&q=80'
  }];
  const trucks = [{
    name: 'Ford F-150 Accessible',
    year: '2024',
    price: '$75/hr',
    description: 'Rugged capability with specialized lift system for driver or passenger.',
    image: 'https://images.unsplash.com/photo-1583267746897-2cf415887172?w=600&q=80'
  }, {
    name: 'Chevrolet Silverado Modified',
    year: '2023',
    price: '$70/hr',
    description: 'Heavy-duty truck featuring an automated wheelchair lift.',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=600&q=80'
  }, {
    name: 'RAM 1500 Accessible',
    year: '2025',
    price: '$78/hr',
    description: 'Luxury interior with seamless mobility modifications and hand controls.',
    image: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=600&q=80'
  }, {
    name: 'Toyota Tundra Modified',
    year: '2022',
    price: '$68/hr',
    description: 'Reliable full-size truck with custom transfer seat installation.',
    image: 'https://images.unsplash.com/photo-1612544448445-b8232cff3b6c?w=600&q=80'
  }];
  const suvs = [{
    name: 'Ford Explorer Accessible',
    year: '2024',
    price: '$65/hr',
    description: 'Spacious SUV with customized rear-entry wheelchair ramp.',
    image: 'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=600&q=80'
  }, {
    name: 'Chevrolet Traverse Modified',
    year: '2023',
    price: '$60/hr',
    description: 'Mid-size crossover with lowered floor and side-entry access.',
    image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=600&q=80'
  }, {
    name: 'Honda Pilot WAV',
    year: '2025',
    price: '$68/hr',
    description: 'Family-friendly SUV featuring a power lift and securement system.',
    image: 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=600&q=80'
  }, {
    name: 'Hyundai Palisade Accessible',
    year: '2024',
    price: '$70/hr',
    description: 'Premium crossover offering luxurious mobility with advanced safety.',
    image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&q=80'
  }];
  return <div className="bg-white min-h-screen" data-id="element-385">
      {/* Page Header */}
      <div className="bg-white py-16 border-b border-warm-200" data-id="element-386">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-387">
          <h1 className="text-4xl md:text-5xl font-extrabold text-black mb-4" data-id="element-388">
            Our Fleet
          </h1>
          <p className="text-xl text-warm-600 max-w-2xl mx-auto" data-id="element-389">
            Discover our diverse range of fully modified, wheelchair-accessible
            vehicles designed for your comfort and safety.
          </p>
        </div>
      </div>

      {/* Section 1: Cars */}
      <section className="py-20 bg-white" data-id="element-390">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-391">
          <div className="mb-12" data-id="element-392">
            <h2 className="text-3xl font-bold text-black mb-4" data-id="element-393">
              Wheelchair Accessible Cars
            </h2>
            <p className="text-lg text-warm-600 max-w-3xl" data-id="element-394">
              Perfect for everyday errands and medical appointments, our
              accessible cars offer a smooth, efficient ride with specialized
              modifications for easy transfer and securement.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-395">
            {cars.map((vehicle, idx) => <VehicleCard key={idx} vehicle={vehicle} data-id="element-396" />)}
          </div>
          <div className="text-center" data-id="element-397">
            <Link to="/fleet/cars" data-id="element-398">
              <Button variant="outline" size="lg" className="font-bold" data-id="element-399">
                See More Cars <ArrowRight className="ml-2 w-5 h-5" data-id="element-400" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 2: Vans */}
      <section className="py-20 bg-warm-50" data-id="element-401">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-402">
          <div className="mb-12" data-id="element-403">
            <h2 className="text-3xl font-bold text-black mb-4" data-id="element-404">
              Wheelchair Accessible Vans
            </h2>
            <p className="text-lg text-warm-600 max-w-3xl" data-id="element-405">
              Our flagship accessible vans provide maximum interior space,
              lowered floors, and power ramps, allowing passengers to remain
              comfortably in their wheelchairs during transit.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-406">
            {vans.map((vehicle, idx) => <VehicleCard key={idx} vehicle={vehicle} data-id="element-407" />)}
          </div>
          <div className="text-center" data-id="element-408">
            <Link to="/fleet/vans" data-id="element-409">
              <Button variant="outline" size="lg" className="font-bold bg-white" data-id="element-410">
                See More Vans <ArrowRight className="ml-2 w-5 h-5" data-id="element-411" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 3: Trucks */}
      <section className="py-20 bg-white" data-id="element-412">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-413">
          <div className="mb-12" data-id="element-414">
            <h2 className="text-3xl font-bold text-black mb-4" data-id="element-415">
              Wheelchair Accessible Trucks
            </h2>
            <p className="text-lg text-warm-600 max-w-3xl" data-id="element-416">
              For those who need rugged capability without compromising on
              accessibility. Our modified trucks feature specialized lift
              systems and hand controls.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-417">
            {trucks.map((vehicle, idx) => <VehicleCard key={idx} vehicle={vehicle} data-id="element-418" />)}
          </div>
          <div className="text-center" data-id="element-419">
            <Link to="/fleet/trucks" data-id="element-420">
              <Button variant="outline" size="lg" className="font-bold" data-id="element-421">
                See More Trucks <ArrowRight className="ml-2 w-5 h-5" data-id="element-422" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 4: SUVs & Crossovers */}
      <section className="py-20 bg-warm-50" data-id="element-423">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-424">
          <div className="mb-12" data-id="element-425">
            <h2 className="text-3xl font-bold text-black mb-4" data-id="element-426">
              Wheelchair Accessible SUVs &amp; Crossovers
            </h2>
            <p className="text-lg text-warm-600 max-w-3xl" data-id="element-427">
              SUVs and crossovers offer the perfect blend of accessibility and
              style, with higher ground clearance and spacious interiors
              modified for wheelchair users.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-428">
            {suvs.map((vehicle, idx) => <VehicleCard key={idx} vehicle={vehicle} data-id="element-429" />)}
          </div>
          <div className="text-center" data-id="element-430">
            <Link to="/fleet/suvs" data-id="element-431">
              <Button variant="outline" size="lg" className="font-bold bg-white" data-id="element-432">
                See More SUVs <ArrowRight className="ml-2 w-5 h-5" data-id="element-433" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>;
}